/**
 * Formatting + parsing helpers shared by the utility-bill surfaces.
 *
 * Extracted verbatim from UtilityDetails.jsx when the bulk-import card landed:
 * both the bill table and the import card render the same dates, money and
 * usage values, and the timezone rules below are subtle enough that a second
 * copy would drift silently. Pure functions only — no React, no services.
 */

/**
 * Shared between parseLocalDate and formatDate so the two always agree on what
 * counts as a "date-only" string (no zone info → handle in local). Any future
 * tweak (e.g. accepting compact `YYYYMMDD`) updates both at once.
 */
export const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Shape AND calendar validity. The regex alone accepts '2026-02-30', which
 * `Date` silently rolls forward into March — filing a bill under the wrong
 * month and skewing the overlap and decimal-shift advisories that key off it.
 */
export const isIsoDate = (value) => {
  if (typeof value !== 'string' || !ISO_DATE_RE.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const probe = new Date(year, month - 1, day);
  return (
    probe.getFullYear() === year &&
    probe.getMonth() === month - 1 &&
    probe.getDate() === day
  );
};

/**
 * Parse a YYYY-MM-DD string in the user's local timezone. `new Date('2026-01-01')`
 * is treated as UTC midnight by the JS spec, which renders as 2025-12-31 in any
 * zone west of UTC.
 */
export const parseLocalDate = (d) => {
  if (!d) return null;
  if (ISO_DATE_RE.test(d)) {
    // Reject rather than roll forward: an out-of-range day must not quietly
    // become a real date in the following month.
    return isIsoDate(d) ? new Date(`${d}T00:00:00`) : null;
  }
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDate = (d) => {
  if (!d) return '';
  const date = parseLocalDate(d);
  if (!date) return d;
  // Two distinct date shapes flow into this helper:
  //   1. ISO YYYY-MM-DD — typed by the user in a <input type="date"> and
  //      echoed straight back; carries NO timezone. parseLocalDate forces
  //      this to local midnight; format in LOCAL so the calendar day the
  //      user typed survives in every zone. (UTC formatting here would
  //      render the prior day for any user east of UTC, e.g. Berlin/Sydney.)
  //   2. RFC-1123 GMT — what Flask's jsonify emits for Python `date`
  //      columns, e.g. "Sun, 01 Feb 2026 00:00:00 GMT". Explicitly UTC;
  //      format in UTC so the calendar day matches the wire value (without
  //      this, viewers west of UTC would render the prior day).
  const isISO = isIsoDate(d);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(isISO ? {} : { timeZone: 'UTC' }),
  });
};

export const formatDateRange = (start, end) => {
  if (!start && !end) return '—';
  return `${formatDate(start)} – ${formatDate(end)}`;
};

/**
 * Convert an API date (ISO YYYY-MM-DD or the RFC-1123 GMT string jsonify emits
 * for Python `date` columns) to the YYYY-MM-DD value an <input type="date">
 * requires. RFC-1123 values are explicitly UTC, so slice the ISO string (also
 * UTC) to keep the wire calendar day — same rule formatDate applies.
 */
export const toDateInputValue = (d) => {
  if (!d) return '';
  // A shape-valid but calendar-invalid value would be handed straight to an
  // <input type="date">, which silently renders it blank while the form still
  // believes it holds a value.
  if (ISO_DATE_RE.test(d)) return isIsoDate(d) ? d : '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

export const formatCurrency = (value, fractionDigits = 2) => {
  if (value == null || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(num);
};

// minimumFractionDigits must be explicit: the compact-currency default varies by JS
// engine (Node 20 prints "$1.0K", Node 24+ "$1K"), and this runs in users' browsers.
const compactUsd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

/**
 * Amount for a narrow month tile (bill coverage strip, statement grid): whole
 * dollars, switching to "$1.2K" from $1,000. Tiles are ~37px wide at the
 * narrowest desktop layout (1024px viewport with the sidebar), so never cents:
 * exact values belong in the tile's accessible label. Accepts numbers or the
 * API's decimal strings.
 */
export const formatTileAmount = (value) => {
  if (value == null || value === '') return null;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(num)) return null;
  const rounded = Math.round(num);
  // Round first: compact notation leaves 999.6 as "$999.6" instead of "$1K".
  return rounded >= 1000 ? compactUsd.format(rounded) : formatCurrency(num, 0);
};

export const formatUsage = (quantity, unit) => {
  if (quantity == null || quantity === '') return '—';
  const num = typeof quantity === 'string' ? parseFloat(quantity) : quantity;
  if (Number.isNaN(num)) return '—';
  // Trim trailing zeros from the metered value (850.000 → 850) but keep
  // genuine fractional usage (12.5).
  const formatted = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 3,
  }).format(num);
  return unit ? `${formatted} ${unit}` : formatted;
};

export const isImageFile = (filename = '') =>
  /\.(png|jpg|jpeg|webp|gif)$/i.test(filename);

/**
 * Route an extracted/hydrated unit into the dropdown-vs-free-text machinery.
 *
 * A case-insensitive match against the type's options selects the canonical
 * option; anything else becomes custom free text. Truncated to the schema's
 * max_length=16 (schemas/utility.py) so a verbose model answer can't cause a
 * 400 round-trip.
 *
 * @param {*} raw - whatever the scan returned for usage_unit
 * @param {string[]} unitOptions - canonical options for this utility type
 * @returns {{unit: string, custom: boolean}|null} null when there is nothing to apply
 */
export const routeExtractedUnit = (raw, unitOptions = []) => {
  if (raw == null) return null;
  const value = String(raw).trim().slice(0, 16);
  if (!value) return null;
  const match = unitOptions.find((u) => u.toLowerCase() === value.toLowerCase());
  return match ? { unit: match, custom: false } : { unit: value, custom: true };
};

/** Bytes → a short human string for file-queue rows. */
export const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
